import React, { useState, useEffect, useRef } from 'react';
import { Box } from '@folksam/ui-next/components';
import InputType from './InputType';
import InputTypeMoped from './InputTypeMoped';
import { useForm } from 'react-final-form';
import { calculateNumberOfVisibleFields, generateSpecialToggleMapping } from '../services/common.service';
import ToggleMappingProvider from '../providers/Toggle';
import { useConfiguration } from '../providers/Configuration';

/**
 * Dynamicaly renders each component in the fields list
 * The InputType component will render the correct Input Component
 */
export default function FormFieldList({ form, fields = [] }) {
    const {
        configuration: {
            componentData: { heading }
        }
    } = useConfiguration();

    const [expandFields, setExpandFields] = useState(false);

    const { subscribe } = useForm();
    useEffect(() => {
        const unsubscribe = subscribe(
            formstate => {
                if (expandFields === false) {
                    setExpandFields(!!formstate.active);
                }
            },
            { active: true }
        );
        return () => {
            unsubscribe();
        };
    }, [expandFields, subscribe]);
    /**
     *
     * we need logic to figure out if we should render 1 or 2 fields
     * if there is more than 3 fields we render 1 field and once the user clicks on of the fields
     * the form is considered active, and we slideout the rest of the fields. We do this to cause a low
     * CLS since SDL is rendering the skeleton of the banner for better performance score on google.
     *
     */

    const numberOfVisibleFields = calculateNumberOfVisibleFields(fields.length);
    const visible = fields.slice(0, numberOfVisibleFields);
    const initiallyHidden = fields.slice(numberOfVisibleFields);

    /**
     * create toggle mapping for legacy json structure
     * we need to check for special case here to generate the correct mappings.
     * this should be done through SDL instead, but its not possible right now.
     */
    const isMopedInsurance = heading.toLowerCase().includes('moped');
    const toggleMapping = generateSpecialToggleMapping(fields, isMopedInsurance);

    const [fieldHeights, setFieldHeights] = useState([]);
    const [tallestFieldHeight, setTallestFieldHeight] = useState(0);
    const refContainer = useRef(null);
    let refs = useRef([React.createRef(), React.createRef()]);

    // Align visible inputs in grid (if the label breaks for one of them) while still have alignItems at start to allow FUN error message to push down elements
    useEffect(() => {
        if (refs.current && visible.length > 1) {
            refs.current[0].current.focus();
            for (let index = 0; index < visible.length; index++) {
                const currentFieldHeight = window
                    .getComputedStyle(refs.current[index].current)
                    .getPropertyValue('height');
                setTallestFieldHeight(prevstate => {
                    return prevstate > currentFieldHeight ? prevstate : currentFieldHeight;
                });
                setFieldHeights(prevstate => {
                    prevstate.push(currentFieldHeight);
                    return [...prevstate];
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const missingFieldHeights = fieldHeights.map(field => {
        return parseFloat(tallestFieldHeight) - parseFloat(field);
    });

    return (
        <ToggleMappingProvider value={toggleMapping}>
            <Box
                sx={{
                    minWidth: ['100%', null, null, initiallyHidden.length > 0 ? '536px' : '432px'],
                    width: ['100%', null, null, initiallyHidden.length > 0 ? '536px' : '432px'],
                    maxWidth: initiallyHidden.length > 0 ? '536px' : '432px',
                    margin: 'auto'
                }}
            >
                <Box
                    sx={{
                        display: 'grid',
                        gap: '3',
                        marginBottom: expandFields ? '4' : 'initial',
                        marginX: 'auto',
                        gridTemplateColumns: ['repeat(1,1fr)', null, null, 'repeat(2,1fr)']
                        //alignItems: 'end'
                    }}
                >
                    {visible.map((field, index) => {
                        const isFirstElementFull = visible.length === 1;
                        return (
                            <Box
                                sx={{
                                    width: ['full', null, null, isFirstElementFull && '50%'],
                                    justifySelf: 'center',
                                    alignItems: `flex-start`,
                                    gridColumn: isFirstElementFull ? '1/-1' : '',
                                    minWidth: 'fit-content',
                                    height: 'fit-content',
                                    '> *': {
                                        marginTop:
                                            refs.current && missingFieldHeights[index] > 0
                                                ? missingFieldHeights[index]
                                                : 0
                                    },
                                    // Override FUN regnr height bug
                                    '> * > div:has(> input):has(> svg)': {
                                        height: '42px'
                                    },
                                    // Override FUN selects (not native select and input with tooltip button)
                                    '> div > div:not(:has(select)) > div:first-of-type:not(:has(button)):not(:has(svg))': {
                                        display: 'inline-block',
                                        overflow: 'hidden',
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis'
                                    },
                                    // Override FUN native select
                                    '> div > div > select': {
                                        whiteSpace: 'nowrap',
                                        textOverflow: 'ellipsis',
                                        paddingRight: '60px'
                                    }
                                }}
                                key={index}
                                ref={refs.current[index]}
                            >
                                <InputType {...field} index={index} toggleMapping={toggleMapping} />
                            </Box>
                        );
                    })}
                </Box>
            </Box>
            {initiallyHidden.length > 0 ? (
                <Box
                    sx={{
                        display: 'grid',
                        height: expandFields ? 'auto' : 0,
                        '> div': {
                            visibility: expandFields ? 'visible' : 'hidden',
                            '> div > label': {
                                maxWidth: ['100%', null, null, 'max-content'],
                                minWidth: ['100%', null, null, 'max-content']
                            }
                        },
                        // Override FUN regnr height bug
                        '> * > div:has(> input):has(> svg)': {
                            height: '42px'
                        },
                        // Override FUN selects (not native select and input with tooltip button)
                        '> div > div:not(:has(select)) > div:first-of-type:not(:has(svg)):not(:has(svg))': {
                            display: 'inline-block',
                            overflow: 'hidden',
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis'
                        },
                        // Override FUN native select
                        '> div > div > select': {
                            whiteSpace: 'nowrap',
                            textOverflow: 'ellipsis',
                            paddingRight: '60px'
                        },
                        maxWidth: ['100%', null, null, '536px'],
                        minWidth: ['100%', null, null, '536px'],
                        gridTemplateColumns: [`1fr`, null, null, `1fr 1fr`],
                        gridGap: '3',
                        marginX: 'auto',
                        alignItems: `flex-start`
                    }}
                    ref={refContainer}
                >
                    {initiallyHidden.map((field, index) => {
                        /**
                         * In order to mimic the old layout.
                         * A 2 column layout is the standard, but if there are more than 2 elements
                         * the first should take up the entire first row, and the rest is in a 2 column grid
                         */

                        return isMopedInsurance ? (
                            <InputTypeMoped {...field} form={form} key={index} toggleMapping={toggleMapping} />
                        ) : (
                            <InputType form={form} {...field} key={index} toggleMapping={toggleMapping} />
                        );
                    })}
                </Box>
            ) : null}
        </ToggleMappingProvider>
    );
}
